import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { initAnalytics } from './config/firebase';
import { AnalyticsDataProvider } from './context/AnalyticsDataContext';
import { VolumeShockerProvider } from './context/VolumeShockerContext';
import { WatchlistProvider } from './context/WatchlistContext';
import { AuthPromptProvider } from './context/AuthPromptContext';
import { AppTourProvider } from './context/AppTourContext';
import { ThemeProvider } from './context/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoadingSpinner from './components/common/LoadingSpinner';
import ErrorBoundary from './components/common/ErrorBoundary';
import { StockDrawerProvider } from './context/StockDrawerContext';
import AppShell from './components/layouts/AppShell';

import { lazyWithRetry } from './utils/lazyWithRetry';

const Login = lazyWithRetry(() => import('./components/Login'), 'login');
const VerifyEmail = lazyWithRetry(() => import('./components/VerifyEmail'), 'verify-email');
const AppTourModal = lazyWithRetry(() => import('./components/common/AppTourModal'), 'app-tour-modal');
const StockQuickPeekDrawer = lazyWithRetry(() => import('./components/common/StockQuickPeekDrawer'), 'stock-quick-peek-drawer');

const Dashboard = lazyWithRetry(() => import('./components/Dashboard'), 'dashboard');
const MarketBreadth = lazyWithRetry(() => import('./components/MarketBreadth'), 'market-breadth');
const VolumeShockers = lazyWithRetry(() => import('./components/VolumeShockers'), 'volume-shockers');
const Accumulation = lazyWithRetry(() => import('./components/Accumulation'), 'accumulation');
const Opportunities = lazyWithRetry(() => import('./components/Opportunities'), 'opportunities');
const PriceMovers = lazyWithRetry(() => import('./components/PriceMovers'), 'price-movers');
const VolumeStreaks = lazyWithRetry(() => import('./components/VolumeStreaks'), 'volume-streaks');
const SummaryOverview = lazyWithRetry(() => import('./components/SummaryOverview'), 'summary-overview');
const Historical = lazyWithRetry(() => import('./components/Historical'), 'historical');
const OverSoldOverBoughtList = lazyWithRetry(() => import('./components/OverboughtOverSold'), 'overbought-oversold');
const IndustryAnalysis = lazyWithRetry(() => import('./components/IndustryAnalysis'), 'industry-analysis');
const SectorExplorer = lazyWithRetry(() => import('./components/SectorExplorer'), 'sector-explorer');
const CorporateFeeds = lazyWithRetry(() => import('./components/CorporateFeeds'), 'corporate-feeds');
const Watchlist = lazyWithRetry(() => import('./components/Watchlist'), 'watchlist');
const BulkBlockDeals = lazyWithRetry(() => import('./components/BulkBlockDeals'), 'bulk-block-deals');
const ContactUs = lazyWithRetry(() => import('./components/ContactUs'), 'contact-us');
const UserGuide = lazyWithRetry(() => import('./components/UserGuide'), 'user-guide');
const AdminUsers = lazyWithRetry(() => import('./components/admin/AdminUsers'), 'admin-users');
const IndexHeatmap = lazyWithRetry(() => import('./components/IndexHeatmap'), 'index-heatmap');
const MtfPositioningPage = lazyWithRetry(() => import('./components/MtfPositioningPage'), 'mtf-positioning');
const ConcallResultCalendar = lazyWithRetry(() => import('./components/ConcallResultCalendar'), 'concall-result-calendar');
const ConcallDetail = lazyWithRetry(() => import('./components/ConcallDetail'), 'concall-detail');

type RouteComponent = React.ComponentType | React.LazyExoticComponent<React.ComponentType>;
type AppRoute = {
  path: string;
  component: RouteComponent;
  adminOnly?: boolean;
};

const routes: AppRoute[] = [
  {
    path: '/',
    component: SummaryOverview,
  },
  {
    path: '/dashboard',
    component: SummaryOverview,
  },
  {
    path: '/overview',
    component: Dashboard,
  },
  {
    path: '/summary',
    component: SummaryOverview,
  },
  {
    path: '/accumulation',
    component: Accumulation,
  },
  {
    path: '/opportunities',
    component: Opportunities,
  },
  {
    path: '/opportunities/short-term',
    component: Opportunities,
  },
  {
    path: '/opportunities/long-term',
    component: Opportunities,
  },
  {
    path: '/opportunities/approaching',
    component: Opportunities,
  },
  {
    path: '/opportunities/super-setups',
    component: Opportunities,
    adminOnly: true,
  },
  {
    path: '/super-setups',
    component: Opportunities,
    adminOnly: true,
  },
  {
    path: '/breakouts',
    component: Opportunities,
  },
  {
    path: '/price-movers',
    component: PriceMovers,
    adminOnly: true,
  },
  {
    path: '/volume-streaks',
    component: VolumeStreaks,
    adminOnly: true,
  },
  {
    path: '/market-breadth',
    component: MarketBreadth,
  },
  {
    path: '/volumeshockers',
    component: VolumeShockers,
  },
  {
    path: '/overboughtsold',
    component: OverSoldOverBoughtList,
  },
  {
    path: '/industry',
    component: IndustryAnalysis,
  },
  {
    path: '/sector',
    component: SectorExplorer,
  },
  {
    path: '/sector/:sectorName',
    component: SectorExplorer,
  },
  {
    path: '/sector-explorer',
    component: SectorExplorer,
  },
  {
    path: '/index-heatmap',
    component: IndexHeatmap,
  },
  {
    path: '/indices-heatmap',
    component: IndexHeatmap,
  },
  {
    path: '/watchlist',
    component: Watchlist,
  },
  {
    path: '/bulk-block-deals',
    component: BulkBlockDeals,
  },
  {
    path: '/bulk-deals',
    component: BulkBlockDeals,
  },
  {
    path: '/corporate-feeds',
    component: CorporateFeeds,
  },
  {
    path: '/results-calendar',
    component: ConcallResultCalendar,
    adminOnly: true,
  },
  {
    path: '/concall-calendar',
    component: ConcallResultCalendar,
    adminOnly: true,
  },
  {
    path: '/earnings-calendar',
    component: ConcallResultCalendar,
    adminOnly: true,
  },
  {
    path: '/concalls',
    component: ConcallResultCalendar,
    adminOnly: true,
  },
  {
    path: '/concalls/:symbol',
    component: ConcallDetail,
    adminOnly: true,
  },
  {
    path: '/mtf-positioning',
    component: MtfPositioningPage,
  },
  {
    path: '/history',
    component: Historical,
  },
  {
    path: '/history/:scriptName',
    component: Historical,
  },
  {
    path: '/stock/:scriptName',
    component: Historical,
  },
  {
    path: '/contact-us',
    component: ContactUs,
  },
  {
    path: '/guide',
    component: UserGuide,
  },
  {
    path: '/playbook',
    component: UserGuide,
  },
  {
    path: '/admin',
    component: AdminUsers,
    adminOnly: true,
  },
  {
    path: '/admin/users',
    component: AdminUsers,
    adminOnly: true,
  },
];

function AppShellRoutes() {
  return (
    <AppShell>
      <ErrorBoundary>
        <Suspense fallback={<LoadingSpinner message="Loading market intelligence desk..." />}>
          <Routes>
            {routes.map((route) => {
              // Publicly accessible pages for search crawlers, guest researchers, and shared watchlist viewing.
              // Admin-only pages still require authentication.
              const isPublicRoute = !route.adminOnly;

              if (isPublicRoute) {
                return (
                  <Route key={route.path} path={route.path} element={<route.component />} />
                );
              }

              return (
                <Route
                  key={route.path}
                  path={route.path}
                  element={
                    <ProtectedRoute adminOnly={route.adminOnly}>
                      <route.component />
                    </ProtectedRoute>
                  }
                />
              );
            })}
            {/* Aliases for legacy & crawler friendly URLs */}
            <Route path="/ema-chart" element={<Navigate to="/market-breadth?tab=ema" replace />} />
            <Route path="/count" element={<Navigate to="/market-breadth?tab=ad" replace />} />
            <Route path="/accumulation-chart" element={<Navigate to="/market-breadth?tab=volume" replace />} />
            <Route path="/breadth" element={<Navigate to="/market-breadth" replace />} />
            <Route path="/contact" element={<Navigate to="/contact-us" replace />} />
            <Route path="/market-map" element={<Navigate to="/overview" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </AppShell>
  );
}

/**
 * Automatically tracks page views across single-page application route transitions
 */
function GoogleAnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
      (window as any).gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location]);

  return null;
}

function App() {
  useEffect(() => {
    void initAnalytics();
  }, []);

  return (
    <Router>
      <GoogleAnalyticsTracker />
      <ErrorBoundary>
        <ThemeProvider>
          <AuthProvider>
            <Routes>
              {/* Public authentication routes */}
              <Route
                path="/login"
                element={
                  <Suspense fallback={<LoadingSpinner message="Loading sign in..." />}>
                    <Login />
                  </Suspense>
                }
              />
              <Route
                path="/verify-email"
                element={
                  <Suspense fallback={<LoadingSpinner message="Verifying session..." />}>
                    <VerifyEmail />
                  </Suspense>
                }
              />

              {/* Application routes - providers wrap the shell so the free dashboard can fetch analytics data */}
              <Route
                path="*"
                element={
                  <AnalyticsDataProvider>
                    <VolumeShockerProvider>
                      <WatchlistProvider>
                        <AuthPromptProvider>
                          <AppTourProvider>
                            <StockDrawerProvider>
                              <Suspense fallback={null}>
                                <AppTourModal />
                                <StockQuickPeekDrawer />
                              </Suspense>
                              <AppShellRoutes />
                            </StockDrawerProvider>
                          </AppTourProvider>
                        </AuthPromptProvider>
                      </WatchlistProvider>

                    </VolumeShockerProvider>
                  </AnalyticsDataProvider>
                }
              />
            </Routes>
          </AuthProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </Router>
  );
}

export default App;
